import {
  Alert, Autocomplete, Box, Button, Chip, Dialog, DialogActions,
  DialogContent, DialogContentText, DialogTitle, Divider, IconButton,
  List, ListItem, ListItemText, MenuItem, Paper, Select, Snackbar,
  TextField, ToggleButton, ToggleButtonGroup, Typography
} from '@mui/material';
import { User } from "../../models/User";
import { Household } from "../../models/Household";
import { HouseholdInvitation } from "../../models/HouseholdInvitation";
import { useTranslation } from "react-i18next";
import PersonIcon from '@mui/icons-material/Person';
import MailIcon from '@mui/icons-material/Mail';
import KeyIcon from '@mui/icons-material/Key';
import LogoutIcon from '@mui/icons-material/Logout';
import { useCallback, useEffect, useState } from "react";
import { changeUserSettings, changePassword, getAllUsernames } from '../../utils/AccountService';
import {
  getCurrentHousehold, createHousehold, leaveHousehold,
  removeHouseholdMember, inviteToHousehold, getPendingInvitations,
  acceptInvitation, declineInvitation
} from '../../utils/HouseholdService';
import { getAppVersion } from '../../utils/MetaService';
import { AppVersion } from '../../models/AppVersion';
import ReleaseNotesDialog from './ReleaseNotesDialog';
import CodeIcon from '@mui/icons-material/Code';
import LightModeIcon from '@mui/icons-material/LightMode';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import TranslateIcon from '@mui/icons-material/Translate';
import FormatSizeIcon from '@mui/icons-material/FormatSize';
import GroupIcon from '@mui/icons-material/Group';
import AddLinkIcon from '@mui/icons-material/AddLink';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import german from '../../assets/german.png';
import uk from '../../assets/united-kingdom.png';
import { UserSettings } from "../../models/UserSettings";
import Cookies from 'js-cookie';

export type UserInformationProps = {
  currentUser: User;
  onLogoutSuccessful: () => void;
  switchColorMode: (colorMode: string) => void;
  changeLanguage: (newLanguage: string) => void;
  changeFontSize: (newFontSize: number) => void;
}

const SectionCard = ({ title, icon, children }: { title: string; icon: React.ReactNode; children: React.ReactNode }) => (
  <Paper elevation={2} sx={{ p: 3, borderRadius: 3, mb: 2 }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2.5 }}>
      <Box sx={{ color: 'primary.main' }}>{icon}</Box>
      <Typography variant="subtitle1" fontWeight={700}>{title}</Typography>
    </Box>
    {children}
  </Paper>
);

const UserInformation = (props: UserInformationProps) => {
  const { t } = useTranslation();
  const [colorMode, setColorMode] = useState(props.currentUser.userSettings.colorMode);
  const [language, setLanguage] = useState(props.currentUser.userSettings.language);
  const [fontSize, setFontSize] = useState(props.currentUser.userSettings.fontSize ?? 14);
  const [openDialog, setOpenDialog] = useState(false);
  const [newPassword, setNewPassword] = useState<string>();
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  const [appVersion, setAppVersion] = useState<AppVersion | null>(null);
  const [releaseNotesOpen, setReleaseNotesOpen] = useState(false);

  const [household, setHousehold] = useState<Household | null>(null);
  const [pendingInvitations, setPendingInvitations] = useState<HouseholdInvitation[]>([]);
  const [newHouseholdName, setNewHouseholdName] = useState('');
  const [availableUsers, setAvailableUsers] = useState<string[]>([]);
  const [newInviteUsername, setNewInviteUsername] = useState<string | null>(null);
  const [householdSnackbarOpen, setHouseholdSnackbarOpen] = useState(false);
  const [householdSnackbarMessage, setHouseholdSnackbarMessage] = useState<string>();

  const loadHousehold = useCallback(() => {
    getCurrentHousehold().then((response: Household | null) => {
      setHousehold(response);
      if (response === null) {
        getPendingInvitations().then(setPendingInvitations);
      }
    });
    getAllUsernames().then(setAvailableUsers);
  }, []);

  useEffect(() => { loadHousehold(); }, [loadHousehold]);

  useEffect(() => { getAppVersion().then(setAppVersion); }, []);

  const createNewHousehold = useCallback(() => {
    if (newHouseholdName) {
      createHousehold(newHouseholdName).then(response => {
        if (response.ok) {
          response.json().then((created: Household) => {
            setHousehold(created);
            setNewHouseholdName('');
            setPendingInvitations([]);
          });
        } else {
          setHouseholdSnackbarMessage(t('couldNotCreateHousehold'));
          setHouseholdSnackbarOpen(true);
        }
      });
    }
  }, [newHouseholdName, t]);

  const leaveCurrentHousehold = useCallback(() => {
    leaveHousehold().then(response => {
      if (response.ok) { setHousehold(null); loadHousehold(); }
      else { setHouseholdSnackbarMessage(t('couldNotLeaveHousehold')); setHouseholdSnackbarOpen(true); }
    });
  }, [t, loadHousehold]);

  const removeMember = useCallback((username: string) => {
    removeHouseholdMember(username).then(response => {
      if (response.ok) {
        setHousehold(curr => curr ? { ...curr, memberUsernames: curr.memberUsernames.filter(m => m !== username) } : curr);
      } else {
        setHouseholdSnackbarMessage(t('couldNotRemoveMember'));
        setHouseholdSnackbarOpen(true);
      }
    });
  }, [t]);

  const inviteUser = useCallback(() => {
    if (newInviteUsername) {
      inviteToHousehold(newInviteUsername).then(response => {
        if (response.ok) setNewInviteUsername(null);
        else { setHouseholdSnackbarMessage(t('couldNotInviteToHousehold')); setHouseholdSnackbarOpen(true); }
      });
    }
  }, [newInviteUsername, t]);

  const respondToInvitation = useCallback((id: number, accept: boolean) => {
    (accept ? acceptInvitation(id) : declineInvitation(id)).then(response => {
      if (response.ok) {
        setPendingInvitations(curr => curr.filter(inv => inv.id !== id));
        if (accept) loadHousehold();
      } else {
        setHouseholdSnackbarMessage(t('couldNotRespondToInvitation'));
        setHouseholdSnackbarOpen(true);
      }
    });
  }, [t, loadHousehold]);

  const logoutCurrentUser = useCallback(() => {
    Cookies.remove('token');
    props.onLogoutSuccessful();
  }, [props]);

  const changeLanguageCallback = useCallback((newLang: string) => {
    setLanguage(newLang);
    props.changeLanguage(newLang);
    changeUserSettings({ ...props.currentUser.userSettings, language: newLang });
  }, [props]);

  const changeColorMode = useCallback((_: any, newMode: string) => {
    setColorMode(old => {
      if (newMode !== null && old !== newMode) {
        props.switchColorMode(newMode);
        changeUserSettings({ ...props.currentUser.userSettings, colorMode: newMode });
        return newMode;
      }
      return old;
    });
  }, [props]);

  const changeFontSizeCallback = useCallback((_: any, newFs: number) => {
    setFontSize(newFs);
    props.changeFontSize(newFs);
    changeUserSettings({ ...props.currentUser.userSettings, fontSize: newFs });
  }, [props]);

  const changeUserPassword = useCallback(() => {
    if (newPassword) {
      changePassword(newPassword).then(response => {
        if (response.ok) setOpenDialog(false);
        else setSnackbarOpen(true);
      });
    } else {
      setPasswordError(t('passwordRequired'));
    }
  }, [newPassword, t]);

  return (
    <Box sx={{ maxWidth: 480, mx: 'auto', px: 2, py: 2, overflowY: 'auto', height: 'calc(100vh - 64px)' }}>

      {/* Profile */}
      <SectionCard title={t('profileSettings')} icon={<PersonIcon />}>
        <TextField
          fullWidth label={t('username')} variant="outlined" sx={{ mb: 2 }}
          value={props.currentUser.username} InputLabelProps={{ shrink: true }} InputProps={{ readOnly: true }}
        />
        <TextField
          fullWidth label={t('email')} variant="outlined" sx={{ mb: 2 }}
          value={props.currentUser.email} InputLabelProps={{ shrink: true }} InputProps={{ readOnly: true }}
        />
        <Box sx={{ display: 'flex', gap: 1.5 }}>
          <Button variant="outlined" startIcon={<KeyIcon />} onClick={() => { setNewPassword(undefined); setPasswordError(null); setOpenDialog(true); }} sx={{ flex: 1 }}>
            {t('changePassword')}
          </Button>
          <Button variant="outlined" color="error" startIcon={<LogoutIcon />} onClick={logoutCurrentUser} sx={{ flex: 1 }}>
            {t('logout')}
          </Button>
        </Box>
      </SectionCard>

      {/* App settings */}
      <SectionCard title={t('appSettings')} icon={<CodeIcon />}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <TranslateIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
            <Typography variant="body2" fontWeight={500}>{t('colorScheme')}</Typography>
          </Box>
          <ToggleButtonGroup exclusive size="small" value={colorMode} onChange={changeColorMode}>
            <ToggleButton value="light"><LightModeIcon fontSize="small" /></ToggleButton>
            <ToggleButton value="dark"><DarkModeIcon fontSize="small" /></ToggleButton>
          </ToggleButtonGroup>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <TranslateIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
            <Typography variant="body2" fontWeight={500}>{t('language')}</Typography>
          </Box>
          <Select size="small" value={language} onChange={(e) => changeLanguageCallback(e.target.value)} sx={{ minWidth: 130 }}>
            <MenuItem value="de">
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <img src={german} alt="de" width={20} /> Deutsch
              </Box>
            </MenuItem>
            <MenuItem value="en">
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <img src={uk} alt="en" width={20} /> English
              </Box>
            </MenuItem>
          </Select>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <FormatSizeIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
            <Typography variant="body2" fontWeight={500}>{t('fontSize')}</Typography>
          </Box>
          <ToggleButtonGroup exclusive size="small" value={fontSize} onChange={changeFontSizeCallback}>
            {[12, 14, 16, 18].map(s => (
              <ToggleButton key={s} value={s} sx={{ fontSize: s - 2, px: 1.5 }}>{s}</ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Box>
      </SectionCard>

      {/* Household */}
      <SectionCard title={t('household')} icon={<GroupIcon />}>
        {household === null ? (
          <>
            <Box sx={{ display: 'flex', gap: 1.5, mb: 2 }}>
              <TextField
                size="small" label={t('householdName')} value={newHouseholdName}
                onChange={(e) => setNewHouseholdName(e.target.value)} sx={{ flex: 1 }}
              />
              <Button variant="outlined" startIcon={<AddLinkIcon />} onClick={createNewHousehold}>
                {t('createHousehold')}
              </Button>
            </Box>
            {pendingInvitations.length > 0 && (
              <>
                <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                  {t('invitedBy')}
                </Typography>
                <List disablePadding>
                  {pendingInvitations.map(inv => (
                    <ListItem key={inv.id} disablePadding sx={{
                      mb: 1, p: 1.5, borderRadius: 2,
                      border: '1px solid', borderColor: 'divider',
                      display: 'flex', justifyContent: 'space-between',
                    }}>
                      <ListItemText
                        primary={inv.householdName}
                        secondary={`${t('invitedBy')}: ${inv.invitedByUsername}`}
                        primaryTypographyProps={{ fontWeight: 600, fontSize: '0.9rem' }}
                        secondaryTypographyProps={{ fontSize: '0.78rem' }}
                      />
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <IconButton size="small" color="success" onClick={() => respondToInvitation(inv.id, true)}>
                          <CheckIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small" color="error" onClick={() => respondToInvitation(inv.id, false)}>
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </ListItem>
                  ))}
                </List>
              </>
            )}
          </>
        ) : (
          <>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Typography fontWeight={700}>{household.name}</Typography>
              <Button variant="outlined" color="error" size="small" onClick={leaveCurrentHousehold}>
                {t('leaveHousehold')}
              </Button>
            </Box>
            <List disablePadding sx={{ mb: 2 }}>
              {household.memberUsernames.map(member => (
                <ListItem key={member} disablePadding sx={{ py: 0.5 }}>
                  <ListItemText
                    primary={member}
                    secondary={member === household.ownerUsername ? t('owner') : undefined}
                    primaryTypographyProps={{ fontWeight: member === household.ownerUsername ? 600 : 400 }}
                  />
                  {props.currentUser.username === household.ownerUsername && member !== household.ownerUsername && (
                    <IconButton size="small" onClick={() => removeMember(member)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  )}
                </ListItem>
              ))}
            </List>
            <Divider sx={{ mb: 2 }} />
            <Box sx={{ display: 'flex', gap: 1.5 }}>
              <Autocomplete
                sx={{ flex: 1 }}
                options={availableUsers.filter(u => !household.memberUsernames.includes(u))}
                getOptionLabel={o => o}
                onChange={(_, v) => setNewInviteUsername(v)}
                value={newInviteUsername}
                renderInput={params => <TextField {...params} size="small" label={t('username')} />}
              />
              <Button variant="outlined" startIcon={<AddLinkIcon />} onClick={inviteUser}>
                {t('invite')}
              </Button>
            </Box>
          </>
        )}
      </SectionCard>

      {/* Info */}
      <SectionCard title={t('information')} icon={<CodeIcon />}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" color="text.secondary">{t('version')}</Typography>
            <Chip label={appVersion ? `v${appVersion.version}` : '…'} size="small" sx={{ fontWeight: 600 }} />
          </Box>
          <Button size="small" onClick={() => setReleaseNotesOpen(true)}>{t('releaseNotes')}</Button>
        </Box>
      </SectionCard>

      <ReleaseNotesDialog open={releaseNotesOpen} setOpen={setReleaseNotesOpen} />

      {/* Change password dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)}>
        <DialogTitle>{t('changePasswordTitle')}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>{t('changePasswordContent')}</DialogContentText>
          <TextField
            autoFocus fullWidth type="password" label={t('newPassword')}
            onChange={(e) => setNewPassword(e.target.value)}
            error={!!passwordError} helperText={passwordError}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button variant="contained" color="success" onClick={changeUserPassword}>{t('confirm')}</Button>
          <Button onClick={() => setOpenDialog(false)}>{t('cancel')}</Button>
        </DialogActions>
      </Dialog>

      <Snackbar anchorOrigin={{ vertical: 'top', horizontal: 'center' }} open={snackbarOpen} autoHideDuration={3000} onClose={() => setSnackbarOpen(false)}>
        <Alert severity="error" variant="filled" sx={{ width: '100%' }} onClose={() => setSnackbarOpen(false)}>
          {t('passwordChangeFailed')}
        </Alert>
      </Snackbar>

      <Snackbar anchorOrigin={{ vertical: 'top', horizontal: 'center' }} open={householdSnackbarOpen} autoHideDuration={3000} onClose={() => setHouseholdSnackbarOpen(false)}>
        <Alert severity="error" variant="filled" sx={{ width: '100%' }} onClose={() => setHouseholdSnackbarOpen(false)}>
          {householdSnackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default UserInformation;
